import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import AutocompleteEmpresas from '@/components/AutocompleteEmpresas';
import MyToastShow from '@/components/Layout/MyToast';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { IUser } from '@/redux/models';
import { IEmpresa } from '@/redux/models/empresaTypes';
import { useAppSelector } from '@/redux/store';
import { object, string } from 'zod';


const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '55%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 3,

};

const schema = object({
    nombre: string().min(1, 'El nombre no puede estar vacío'),
    apellido: string().min(1, 'El apellido no puede estar vacío'),
    telefono: string().optional().nullable(),
    email: string().email('Email inválido'),
    role: string().optional().nullable(),
    empresa: string().optional().nullable(),
});

export default function ModalVerUsuarioV2({ open, onClose, user, empresas, editarUsuario }: { open: boolean, onClose: () => void, user: IUser, empresas: IEmpresa[], editarUsuario: (user: IUser) => Promise<void> }) {
    const { user: loggedUser } = useAppSelector(state => state.userState)

    const { register, handleSubmit, formState: { errors }, reset } = useForm<IUser>({
        resolver: zodResolver(schema),
        defaultValues: user,
    })

    const [optionsRole, setOptionsRole] = useState<string[]>(['user', 'admin'])

    const [empresaSelected, setEmpresaSelected] = useState<any>(user?.company || null);

    useEffect(() => {
        if (loggedUser?.role === "dev")
            setOptionsRole(['user', 'admin', 'dev'])

        setEmpresaSelected(user?.company || null)
        return () => {
            setEmpresaSelected(null)
            reset()
        }
    }, [user, loggedUser])

    const onSubmitHandler: SubmitHandler<IUser> = (values) => {
        const { company, ...rest } = user

        editarUsuario({
            ...rest, company: {
                id: empresaSelected?.id || null
            } as any, ...values
        }).then(() => {
            MyToastShow({ message: 'Usuario actualizado correctamente', type: 'success', autoClose: 1500 })
        }).catch((error: any) => {
            console.error('Error al actualizar el usuario:', error);
        })
    };


    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: {
                        enter: 700,
                        appear: 500,
                        exit: 300,
                    }
                },
            }}
        >
            <Fade in={open}>
                <Box sx={style}>
                    <Typography id="transition-modal-title" variant="h6" component="h2">
                        Detalles
                    </Typography>
                    <Grid container spacing={2} sx={{ pt: '2rem' }} component='form'
                        onSubmit={handleSubmit(onSubmitHandler)}
                    >
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Nombre"
                                variant="outlined"
                                defaultValue={user.nombre}
                                {...register('nombre')}
                                error={!!errors.nombre}
                                helperText={errors.nombre?.message}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Apellido"
                                defaultValue={user.apellido}
                                variant="outlined"
                                {...register('apellido')}
                                error={!!errors.apellido}
                                helperText={errors.apellido?.message}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AutocompleteEmpresas
                                sx={{ width: '100%' }}
                                empresa={empresaSelected}
                                rows={empresas}
                                loading={false}
                                onChange={(empresa) => setEmpresaSelected(empresa)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                variant="outlined"
                                defaultValue={user.email}
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Telefono"
                                variant="outlined"
                                defaultValue={user.telefono}
                                {...register('telefono')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth sx={{}}>
                                <InputLabel id="lblVel" size='small'>Rol</InputLabel>
                                <Select
                                    {...register('role')}
                                    name="role"
                                    labelId="role"
                                    label="Rol"
                                    variant="outlined"
                                    defaultValue={user.role}
                                >
                                    {optionsRole.map((rol: any) => {
                                        return <MenuItem key={rol} value={rol}>{rol[0].toUpperCase() + rol.substr(1).toLowerCase()}</MenuItem>
                                    })}
                                </Select>

                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Empresa (texto)"
                                variant="outlined"
                                defaultValue={user.empresa}
                                {...register('empresa')}
                                error={!!errors.empresa}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row-reverse', gap: '15px' }}>
                            <Button variant="contained" size="small" color='success' type='submit' sx={{ mt: 2, mr: 1 }}>Actualizar</Button>
                            <Button variant="contained" size="small" color='error' sx={{ mt: 2 }} onClick={onClose}>Cerrar</Button>
                        </Grid>

                    </Grid>
                </Box>
            </Fade>
        </Modal>
    );
}
